import { h, } from "@stencil/core";
import { arrow, autoUpdate, computePosition, flip, offset, hide, } from "@floating-ui/dom";
import { size } from "@floating-ui/core";
import { allowFocus, shadowMutationObserver, getScrollParent, } from "@eventstore-ui/utils";
var PopoverState;
(function (PopoverState) {
  PopoverState[PopoverState["detached"] = 0] = "detached";
  PopoverState[PopoverState["attaching"] = 1] = "attaching";
  PopoverState[PopoverState["attached"] = 2] = "attached";
  PopoverState[PopoverState["entering"] = 3] = "entering";
  PopoverState[PopoverState["closing"] = 4] = "closing";
})(PopoverState || (PopoverState = {}));
/**
 * Attaches a portaled popover, attached to the parent node. Can be used to create dropdowns, tooltips etc. The parent scoped shadow styles are copied to the created portals shadow styles, to allow styling popover contents externally.
 */
export class Popover {
  constructor() {
    this.connected = false;
    this.portalledNodes = [];
    this.state = PopoverState.detached;
    this.adoptStyleRules = ({ from, to, }) => {
      if (!from || !to)
        return;
      for (const sheet of Array.from(from)) {
        const rules = sheet.cssRules;
        for (let i = 0; i < rules.length; i++) {
          const rule = rules.item(i);
          if (rule.selectorText &&
            rule.selectorText.includes('es-popper-inner')) {
            to.insertRule(rule.cssText.replace(/es-popper-inner(\S*)/g, (_, compoundSelector) => compoundSelector
              ? `:host(${compoundSelector})`
              : ':host'), to.cssRules.length);
          }
          else if (!rule.selectorText ||
            !rule.selectorText.includes(':host')) {
            to.insertRule(rule.cssText, to.cssRules.length);
          }
        }
      }
    };
    this.attachPopper = async () => {
      clearTimeout(this.nextStepTimeout);
      switch (this.state) {
        case PopoverState.attached:
        case PopoverState.attaching:
        case PopoverState.entering:
          return;
        case PopoverState.closing:
          return this.cancelClose();
      }
      const target = document.querySelector(this.target);
      if (!target)
        return;
      clearTimeout(this.nextStepTimeout);
      this.state = PopoverState.attaching;
      const parentShadow = this.host.parentNode?.getRootNode();
      const popper = document.createElement('es-popper');
      const shadowStyle = document.createElement('style');
      const popperShadow = document.createElement('es-popper-inner');
      const popperX = document.createElement('es-popper-x');
      const popperY = document.createElement('es-popper-y');
      const popperInner = document.createElement('div');
      const assignedNodes = this.host.shadowRoot.querySelector('slot')?.assignedNodes() ?? [];
      const zIndexBase = getComputedStyle(this.host).getPropertyValue('--zindex-base');
      popper.style.setProperty('--zindex-base', zIndexBase);
      popper.style.opacity = '0';
      if (this.zIndex != null) {
        popper.style.setProperty('--zindex-popover', `${this.zIndex}`);
      }
      popper.setAttribute('backdrop', `${this.backdrop}`);
      popper.setAttribute('trap-focus', `${this.trapFocus}`);
      popperInner.classList.add('inner');
      if (this.popperClass) {
        for (const className of this.popperClass.split(' ')) {
          if (!className.length)
            continue;
          popperInner.classList.add(className);
        }
      }
      target.appendChild(popper);
      popper.appendChild(popperShadow);
      popperShadow.shadowRoot?.appendChild(popperX);
      popperX.appendChild(popperY);
      popperY.appendChild(popperInner);
      popperY.appendChild(shadowStyle);
      if (parentShadow) {
        const shadowSheet = shadowStyle.sheet;
        this.adoptStyleRules({
          from: parentShadow.adoptedStyleSheets,
          to: shadowSheet,
        });
        this.adoptStyleRules({
          from: parentShadow.styleSheets,
          to: shadowSheet,
        });
        if (assignedNodes.find(({ nodeName }) => nodeName === 'SLOT')) {
          const grandParentShadow = parentShadow.host.parentNode?.getRootNode();
          this.adoptStyleRules({
            from: grandParentShadow?.adoptedStyleSheets,
            to: shadowSheet,
          });
          this.adoptStyleRules({
            from: grandParentShadow?.styleSheets,
            to: shadowSheet,
          });
        }
      }
      this.portalledNodes = [];
      for (const node of assignedNodes) {
        if (node.nodeName === 'SLOT') {
          const slot = node;
          for (const slottedNode of slot.assignedNodes()) {
            const placeholder = this.createPlaceholder();
            this.portalledNodes.push([slottedNode, placeholder]);
            slottedNode.parentNode.replaceChild(placeholder, slottedNode);
            popperInner.appendChild(slottedNode);
          }
        }
        else {
          const placeholder = this.createPlaceholder();
          this.portalledNodes.push([node, placeholder]);
          node.parentNode.replaceChild(placeholder, node);
          popperInner.appendChild(node);
        }
      }
      popper.addEventListener('requestClose', this.bubbleRequestClose);
      if (this.closeOnBlur) {
        popperInner.addEventListener('mousedown', (e) => {
          e.preventDefault();
        });
        popperInner.addEventListener('focusout', (e) => {
          if (!this.connected ||
            popperInner.contains(e.relatedTarget)) {
            return;
          }
          this.requestClose.emit('blur');
        });
      }
      const attachment = this.attachTo ?? this.getParentNode();
      this.scrollParent = getScrollParent(attachment);
      this.popper = popper;
      this.popperShadow = popperShadow;
      this.popperX = popperX;
      this.popperY = popperY;
      this.popperInner = popperInner;
      if (this.arrow) {
        const popperArrow = document.createElement('div');
        popperArrow.classList.add('arrow');
        popperY.appendChild(popperArrow);
        this.popperArrow = popperArrow;
      }
      await popper.loaded();
      await this.positionPopper();
      this.attachDocumentListeners();
      clearTimeout(this.nextStepTimeout);
      this.state = PopoverState.entering;
      this.nextStepTimeout = setTimeout(this.enterPopper, 50);
    };
    this.attachDocumentListeners = () => {
      if (!this.popperInner)
        return;
      this.detachAllowFocus = allowFocus(this.popperInner);
      this.autoUpdateCleanup = autoUpdate(this.attachTo ?? this.getParentNode(), this.popperShadow, () => this.positionPopper());
      window.addEventListener('es-layout-change', this.positionPopper);
    };
    this.detachDocumentListeners = () => {
      this.detachAllowFocus?.();
      this.autoUpdateCleanup?.();
      this.autoUpdateCleanup = undefined;
      window.removeEventListener('es-layout-change', this.positionPopper);
    };
    this.positionPopper = async () => {
      if (!this.open || !this.popperShadow)
        return;
      const attachment = this.attachTo ?? this.getParentNode();
      const location = await computePosition(attachment, this.popperShadow, {
        placement: this.placement,
        strategy: 'fixed',
        middleware: [
          offset(() => ({
            mainAxis: this.offset,
          })),
          ...(this.flip
            ? [
              flip({
                fallbackStrategy: 'initialPlacement',
                fallbackPlacements: Array.isArray(this.flip)
                  ? this.flip
                  : undefined,
              }),
            ]
            : []),
          size({
            padding: 10,
            apply: ({ width, height, reference }) => {
              if (!this.popperShadow || !this.popperInner)
                return;
              const as = this.autoSize;
              if (as === 'height' || as === 'both') {
                this.popperShadow.style.height = `${reference.height}px`;
              }
              if (as === 'width' || as === 'both') {
                this.popperShadow.style.width = `${reference.width}px`;
              }
              const c = this.constrain;
              if (c === 'height' || c === 'both') {
                this.popperInner.style.maxHeight = `${Math.min(height, this.maxHeight)}px`;
              }
              if (c === 'width' || c === 'both') {
                this.popperInner.style.maxWidth = `${Math.min(width, this.maxWidth)}px`;
              }
            },
          }),
          ...(this.popperArrow
            ? [
              arrow({
                element: this.popperArrow,
              }),
            ]
            : []),
          ...(this.scrollParent
            ? [
              hide({
                boundary: this.scrollParent,
              }),
            ]
            : []),
        ],
      });
      if (!this.open ||
        !this.popperShadow ||
        !this.popperInner ||
        !this.popperX ||
        !this.popperY) {
        return;
      }
      this.popperInner.dataset.placement = location.placement;
      this.popperShadow.dataset.placement = location.placement;
      this.popperX.style.transform = `translateX(${location.x}px)`;
      this.popperY.style.transform = `translateY(${location.y}px)`;
      if (this.popperArrow && location.middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = location.middlewareData.arrow;
        this.popperArrow.dataset.placement = location.placement;
        Object.assign(this.popperArrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
        });
      }
      if (this.closeOnScrollEscape &&
        location.middlewareData.hide?.referenceHidden) {
        this.requestClose.emit('scrollEscape');
      }
      if (this.hideOnScrollEscape) {
        if (location.middlewareData.hide?.referenceHidden) {
          this.popperShadow.classList.add('hidden');
        }
        else {
          this.popperShadow.classList.remove('hidden');
        }
      }
    };
    this.enterPopper = () => {
      this.popperInner?.classList.add('entered');
      this.popperArrow?.classList.add('entered');
      this.popperShadow?.classList.add('entered');
      if (this.popper) {
        this.popper.style.opacity = '1';
      }
      this.state = PopoverState.attached;
      return this.positionPopper();
    };
    this.cancelClose = () => {
      clearTimeout(this.nextStepTimeout);
      this.popperInner?.classList.remove('exiting');
      this.popperArrow?.classList.remove('exiting');
      this.popperShadow?.classList.remove('exiting');
      this.enterPopper();
    };
    this.closePopper = (animate = true) => {
      switch (this.state) {
        case PopoverState.detached:
        case PopoverState.closing:
          return;
      }
      this.popperInner?.classList.add('exiting');
      this.popperInner?.classList.remove('entered');
      this.popperArrow?.classList.add('exiting');
      this.popperArrow?.classList.remove('entered');
      this.popperShadow?.classList.add('exiting');
      this.popperShadow?.classList.remove('entered');
      clearTimeout(this.nextStepTimeout);
      this.state = PopoverState.closing;
      if (animate) {
        this.nextStepTimeout = setTimeout(this.detachPopper, 400);
      }
      else {
        this.detachPopper();
      }
    };
    this.detachPopper = () => {
      if (!this.popper || this.state === PopoverState.detached)
        return;
      this.detachDocumentListeners();
      for (const [node, placeholder] of this.portalledNodes) {
        placeholder.parentNode?.replaceChild(node, placeholder);
      }
      this.portalledNodes = [];
      delete this.scrollParent;
      this.popper.parentNode?.removeChild(this.popper);
      this.popper = undefined;
      this.popperShadow = undefined;
      this.popperX = undefined;
      this.popperY = undefined;
      this.popperInner = undefined;
      this.popperArrow = undefined;
      this.state = PopoverState.detached;
    };
    this.bubbleRequestClose = (e) => {
      if (!this.connected)
        return;
      this.requestClose.emit(e.detail);
    };
    this.getParentNode = () => {
      const parent = this.host.parentNode;
      if (!parent)
        return null;
      if ('host' in parent) {
        return parent.host;
      }
      return parent;
    };
    this.createPlaceholder = () => {
      const id = Math.random().toString(16).slice(2);
      return document.createComment(id);
    };
    this.popperClass = undefined;
    this.target = 'body';
    this.open = false;
    this.backdrop = false;
    this.zIndex = undefined;
    this.arrow = false;
    this.trapFocus = false;
    this.closeOnBlur = false;
    this.closeOnClickOutside = false;
    this.closeOnEsc = false;
    this.closeOnScrollEscape = false;
    this.hideOnScrollEscape = true;
    this.attachTo = undefined;
    this.autoSize = 'none';
    this.constrain = 'height';
    this.maxHeight = Infinity;
    this.maxWidth = Infinity;
    this.placement = 'top';
    this.flip = true;
    this.offset = 0;
  }
  componentDidLoad() {
    this.connected = true;
    this.mutationObserver = shadowMutationObserver(this.host, this.positionPopper);
    if (this.open) {
      this.attachPopper();
    }
  }
  disconnectedCallback() {
    this.connected = false;
    this.mutationObserver?.disconnect();
    if (this.open) {
      this.closePopper(false);
    }
  }
  setPopperClass(popperClass, previousClass) {
    if (!this.popperInner)
      return;
    if (previousClass) {
      for (const className of previousClass.split(' ')) {
        if (!className.length)
          continue;
        this.popperInner.classList.remove(className);
      }
    }
    if (popperClass) {
      for (const className of popperClass.split(' ')) {
        if (!className.length)
          continue;
        this.popperInner.classList.remove(className);
      }
    }
  }
  togglePopover(isOpen) {
    if (isOpen) {
      this.attachPopper();
    }
    else {
      this.closePopper();
    }
  }
  onClickOutside(e) {
    if (!this.closeOnClickOutside)
      return;
    if (!this.open || !this.popperInner)
      return;
    const path = e.composedPath();
    if (path.includes(this.host) || path.includes(this.popperInner))
      return;
    this.requestClose.emit();
  }
  onEscPress(e) {
    if (!this.closeOnEsc)
      return;
    if (e.key !== 'Escape' || e.altKey || e.ctrlKey || e.metaKey)
      return;
    this.requestClose.emit();
  }
  render() {
    return h("slot", null);
  }
  static get is() { return "es-popover"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-popover.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-popover.css"]
    };
  }
  static get properties() {
    return {
      "popperClass": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Class name for the popper"
        },
        "attribute": "popper-class",
        "reflect": false
      },
      "target": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A query selecter to select the element to portal the popper to."
        },
        "attribute": "target",
        "reflect": false,
        "defaultValue": "'body'"
      },
      "open": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Toggles if the popover is open or not."
        },
        "attribute": "open",
        "reflect": true,
        "defaultValue": "false"
      },
      "backdrop": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should overlay a backdrop, to prevent external clicks."
        },
        "attribute": "backdrop",
        "reflect": false,
        "defaultValue": "false"
      },
      "zIndex": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "What zIndex to place the popover in."
        },
        "attribute": "z-index",
        "reflect": false
      },
      "arrow": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should render an arrow."
        },
        "attribute": "arrow",
        "reflect": false,
        "defaultValue": "false"
      },
      "trapFocus": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should trap focus within, and return focus on close."
        },
        "attribute": "trap-focus",
        "reflect": false,
        "defaultValue": "false"
      },
      "closeOnBlur": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should request to close when focus is lost"
        },
        "attribute": "close-on-blur",
        "reflect": false,
        "defaultValue": "false"
      },
      "closeOnClickOutside": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should request to close when clicked outside"
        },
        "attribute": "close-on-click-outside",
        "reflect": false,
        "defaultValue": "false"
      },
      "closeOnEsc": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should request to close when esc is pressed"
        },
        "attribute": "close-on-esc",
        "reflect": false,
        "defaultValue": "false"
      },
      "closeOnScrollEscape": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should request to close when the attachment element scrolls out of view"
        },
        "attribute": "close-on-scroll-escape",
        "reflect": false,
        "defaultValue": "false"
      },
      "hideOnScrollEscape": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the popover should hide itself when the attachment element scrolls out of view"
        },
        "attribute": "hide-on-scroll-escape",
        "reflect": false,
        "defaultValue": "true"
      },
      "attachTo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "HTMLElement",
          "resolved": "HTMLElement | undefined",
          "references": {
            "HTMLElement": {
              "location": "global",
              "id": "global::HTMLElement"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Pass an element to attach the popover to. (Defaults to the parent element.)"
        }
      },
      "autoSize": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "Constrain",
          "resolved": "\"both\" | \"height\" | \"none\" | \"width\"",
          "references": {
            "Constrain": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-popover/types.ts::Constrain"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Constrain the size of the popover to the size of the attachment node."
        },
        "attribute": "auto-size",
        "reflect": false,
        "defaultValue": "'none'"
      },
      "constrain": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "Constrain",
          "resolved": "\"both\" | \"height\" | \"none\" | \"width\"",
          "references": {
            "Constrain": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-popover/types.ts::Constrain"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Constrain the size of the popover inner to the size of the window."
        },
        "attribute": "constrain",
        "reflect": false,
        "defaultValue": "'height'"
      },
      "maxHeight": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The maximum height to constrain to."
        },
        "attribute": "max-height",
        "reflect": false,
        "defaultValue": "Infinity"
      },
      "maxWidth": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The maximum width to constrain to."
        },
        "attribute": "max-width",
        "reflect": false,
        "defaultValue": "Infinity"
      },
      "placement": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "Placement",
          "resolved": "\"bottom\" | \"bottom-end\" | \"bottom-start\" | \"left\" | \"left-end\" | \"left-start\" | \"right\" | \"right-end\" | \"right-start\" | \"top\" | \"top-end\" | \"top-start\"",
          "references": {
            "Placement": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-popover/types.ts::Placement"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Where to place the popover in relation to the attachment point."
        },
        "attribute": "placement",
        "reflect": false,
        "defaultValue": "'top'"
      },
      "flip": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "Placement[] | boolean",
          "resolved": "Placement[] | boolean | undefined",
          "references": {
            "Placement": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-popover/types.ts::Placement"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "An array of allowed placements or enable / disable"
        },
        "attribute": "flip",
        "reflect": false,
        "defaultValue": "true"
      },
      "offset": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The offset away from the attachement element in px."
        },
        "attribute": "offset",
        "reflect": false,
        "defaultValue": "0"
      }
    };
  }
  static get events() {
    return [{
        "method": "requestClose",
        "name": "requestClose",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggers when the popover requests to close."
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }];
  }
  static get elementRef() { return "host"; }
  static get watchers() {
    return [{
        "propName": "popperClass",
        "methodName": "setPopperClass"
      }, {
        "propName": "open",
        "methodName": "togglePopover"
      }];
  }
  static get listeners() {
    return [{
        "name": "click",
        "method": "onClickOutside",
        "target": "document",
        "capture": false,
        "passive": false
      }, {
        "name": "keydown",
        "method": "onEscPress",
        "target": "document",
        "capture": true,
        "passive": false
      }];
  }
}
//# sourceMappingURL=es-popover.js.map
